import React from 'react';
import { Card, Statistic, Button, Typography, theme } from 'antd';
import type { ReactNode } from 'react';
import { Sparkline } from '@/components/sales/control-tower/Sparkline';
import type { KpiTrendPoint } from '@/services/salesDashboard.service';

const { Text } = Typography;

export type ControlTowerMetricUrgency = 'critical' | 'attention' | 'neutral';

export interface ControlTowerMetricTileProps {
  title: string;
  count: number;
  amount?: number;
  formatCurrency?: (v: number) => string;
  icon?: ReactNode;
  urgency?: ControlTowerMetricUrgency;
  footerLink?: { label: string; onClick: () => void };
  extra?: ReactNode;
  valueDisplay?: string;
  trend?: KpiTrendPoint[];
}

export const ControlTowerMetricTile: React.FC<ControlTowerMetricTileProps> = ({
  title,
  count,
  amount,
  formatCurrency,
  icon,
  urgency = 'neutral',
  footerLink,
  extra,
  valueDisplay,
  trend,
}) => {
  const { token } = theme.useToken();
  const borderColor =
    urgency === 'critical'
      ? token.colorError
      : urgency === 'attention'
        ? token.colorWarning
        : token.colorBorderSecondary;

  const valueStyle =
    urgency === 'critical'
      ? { color: token.colorError }
      : urgency === 'attention'
        ? { color: token.colorWarning }
        : undefined;

  return (
    <Card
      size="small"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        height: '100%',
        background: token.colorBgContainer,
      }}
    >
      <Statistic
        title={title}
        value={valueDisplay ?? count}
        prefix={icon}
        valueStyle={valueDisplay ? { fontSize: 18, ...valueStyle } : valueStyle}
      />
      {amount != null && formatCurrency && (
        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 16 }}>
          {formatCurrency(amount)}
        </Text>
      )}
      {trend && trend.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <Sparkline points={trend} />
        </div>
      )}
      {extra}
      {footerLink && (
        <Button type="link" size="small" style={{ padding: 0, marginTop: 4 }} onClick={footerLink.onClick}>
          {footerLink.label}
        </Button>
      )}
    </Card>
  );
};
